import React, { useMemo, useState } from 'react';
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
    type MRT_TableOptions,
    MRT_ShowHideColumnsButton,
    MRT_ToggleGlobalFilterButton,
} from 'material-react-table';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    TextField,
    Grid2 as Grid,
    Autocomplete,
    Chip,
    Popover,
    Box as MuiBox
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { CloudDownload } from '@mui/icons-material';
import { mkConfig, generateCsv, download } from 'export-to-csv';

export interface ISharedTableColumn<T> {
    accessorKey: keyof T;
    header: string;
    size?: number;
    grow?: boolean | number;
    enableSorting?: boolean;
    enableFiltering?: boolean;
    enableColumnActions?: boolean;
    Cell?: ({ cell, row }: { cell: any; row: any }) => React.ReactNode;
    filterVariant?: 'text' | 'select' | 'multi-select' | 'range' | 'date' | 'date-range';
    filterSelectOptions?: string[];
}

export interface ISharedTableProps<T> {
    data: T[];
    columns: ISharedTableColumn<T>[];
    title?: string;
    isLoading?: boolean;
    enablePagination?: boolean;
    enableSorting?: boolean;
    enableFiltering?: boolean;
    enableColumnActions?: boolean;
    enableRowActions?: boolean;
    enableTopToolbar?: boolean;
    enableBottomToolbar?: boolean;
    enableRowSelection?: boolean;
    enableMultiRowSelection?: boolean;
    rowSelection?: Record<string, boolean>;
    onRowSelectionChange?: (updatedSelection: Record<string, boolean>) => void;
    getRowId?: (row: T, index: number) => string;
    setTableFilters?: (filters: Array<{ id: string; value: any }>) => void;
    enableSelectAll?: boolean;
    selectAllMode?: 'all' | 'page';
    isRowSelectable?: (row: T) => boolean;
    initialState?: {
        pagination?: {
            pageIndex: number;
            pageSize: number;
        };
        sorting?: Array<{
            id: string;
            desc: boolean;
        }>;
        columnVisibility?: Record<string, boolean>;
        columnFilters?: Array<{ id: string; value: any }>;
    };
    renderRowActions?: ({ row }: { row: any }) => React.ReactNode;
    renderTopToolbarCustomActions?: () => React.ReactNode;
    onRowClick?: (row: any) => void;
    muiTableProps?: any;
    muiTableBodyRowProps?: any;
    muiTableHeadCellProps?: any;
    muiTableBodyCellProps?: any;
    muiPaginationProps?: any;
    emptyMessage?: string;
    layoutMode?: 'auto' | 'fixed';
    enableColumnResizing?: boolean;
    enableFilterModal?: boolean;
    enableDownloadCsv?: boolean;
    downloadFileName?: string;
    enableShowHideColumns?: boolean;
    enableGlobalSearch?: boolean;
    filterModalTitle?: string;
    // Deprecated: filterModalColumnIds is ignored; all columns with enableFiltering=true are shown in the popover
    filterModalColumnIds?: string[];
}

export function SharedMaterialReactTable<T extends Record<string, any>>({
    data,
    columns,
    title,
    isLoading = false,
    enablePagination = true,
    enableSorting = true,
    enableFiltering = true,
    enableColumnActions = false,
    enableRowActions = false,
    enableTopToolbar = true,
    enableBottomToolbar = true,
    enableRowSelection = false,
    enableMultiRowSelection = true,
    rowSelection,
    onRowSelectionChange,
    getRowId,
    setTableFilters,
    enableSelectAll = true,
    selectAllMode = 'all',
    isRowSelectable,
    initialState,
    renderRowActions,
    renderTopToolbarCustomActions,
    onRowClick,
    muiTableProps,
    muiTableBodyRowProps,
    muiTableHeadCellProps,
    muiTableBodyCellProps,
    muiPaginationProps,
    emptyMessage = 'No data found.',
    layoutMode = 'fixed',
    enableColumnResizing = false,
    enableFilterModal = false,
    enableDownloadCsv = false,
    downloadFileName = title ? title.replace(/\s+/g, '_') : 'table_data',
    enableShowHideColumns = false,
    enableGlobalSearch = true,
    filterModalTitle = 'Filters',
    filterModalColumnIds 
}: ISharedTableProps<T>) {
    // Filter popover state
    const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
    const isFilterPopoverOpen = Boolean(filterAnchorEl);

    const memoizedColumns = useMemo<MRT_ColumnDef<T>[]>
        (() =>
            columns.map((col) => ({
                accessorKey: col.accessorKey as string,
                header: col.header,
                // Treat provided size as the max width; content will wrap
                size: col.size,
                // Remove maxSize to allow natural wrapping
                // don't force min size; allow shrinking to fit
                grow: col.grow ?? true,
                enableSorting: col.enableSorting ?? enableSorting,
                // Respect individual column enableFiltering settings
                enableColumnFilter: col.enableFiltering ?? enableFiltering,
                // Hide all column action menus (three-dots) per requirement
                enableColumnActions: false,
                Cell: col.Cell,
                filterVariant: col.filterVariant,
                filterSelectOptions: col.filterSelectOptions,
                // Enforce wrapping and max width at cell level
                muiTableHeadCellProps: {
                    sx: {
                        ...(col.size ? { maxWidth: col.size, width: col.size } : {}),
                        whiteSpace: 'normal !important',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        wordBreak: 'normal',
                        textOverflow: 'clip !important',
                        overflow: 'visible !important',
                    },
                },
                muiTableBodyCellProps: {
                    sx: {
                        ...(col.size ? { maxWidth: col.size } : {}),
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                    },
                },
                // Custom filter function for multi-select arrays
                filterFn: enableFilterModal && (col.enableFiltering ?? enableFiltering)
                    ? (row, columnId, filterValue) => {
                        if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) {
                            return true; // Show all rows if no filter
                        }
                        const cellValue = String(row.getValue(columnId) || '');
                        return filterValue.includes(cellValue);
                    }
                    : undefined,
            })),
            [columns, enableSorting, enableFiltering, enableColumnActions, layoutMode, enableFilterModal]
        );
    // CSV Download handler
    const csvConfig = mkConfig({
        fieldSeparator: ',',
        decimalSeparator: '.',
        useKeysAsHeaders: true,
        filename: downloadFileName
    });
    const handleDownloadCsv = () => {
        // Get visible columns (with accessorKey)
        const visibleColumns = table.getVisibleLeafColumns()
            .filter(col => typeof col.id === 'string' && col.id.length > 0 
                && !col.id.toLowerCase().startsWith('action')
                && !col.id.toLowerCase().startsWith('submissionaccess')
            )
            .map(col => col.id);

        // Capitalize headers
        const headers = visibleColumns.map(s => s.charAt(0).toUpperCase() + s.slice(1));

        // Prepare data as array of objects with capitalized header keys
        const rowData = table.getFilteredRowModel().rows.map((row) => {
            const original = row.original;
            const obj: Record<string, any> = {};
            visibleColumns.forEach((key, idx) => {
                const value = original[key];
                obj[headers[idx]] = value === null ? '' : value;
            });
            return obj;
        });

        // Update csvConfig to use only visible columns as headers
        const csvConfigVisible = {
            ...csvConfig,
            useKeysAsHeaders: true,
            headers: headers
        };

        const csv = generateCsv(csvConfigVisible)(rowData);
        download(csvConfigVisible)(csv);
    };

    const tableOptions: MRT_TableOptions<T> = {
        columns: memoizedColumns,
        data,
        enablePagination,
        enableSorting,
        enableColumnFilters: enableFiltering,
        // Hide all column actions globally per requirement
        enableColumnActions: false,
        enableRowActions,
        enableTopToolbar,
        enableBottomToolbar,
        enableRowSelection,
        enableMultiRowSelection,
        enableSelectAll,
        selectAllMode,
        enableDensityToggle: false,
        enableColumnResizing: layoutMode === 'fixed' ? enableColumnResizing : true,
        columnResizeMode: 'onChange',
        layoutMode: 'semantic', // Force semantic layout to allow proper wrapping
        columnFilterDisplayMode: enableFilterModal ? 'custom' : 'subheader',
        initialState: {
            pagination: { pageIndex: 0, pageSize: 15 },
            density: 'compact',
            showColumnFilters: enableFilterModal ? false : undefined, // Don't show default filters when using modal
            ...initialState,
        },
        getRowId,
        onColumnFiltersChange: setTableFilters
            ? (updater) => {
                const next = typeof updater === 'function'
                    ? updater(table.getState().columnFilters)
                    : updater;
                // Normalize multi-select single values to array for consistency
                const normalized = next.map(f => ({
                    id: f.id,
                    value: Array.isArray(f.value) || f.value === undefined ? f.value : [f.value]
                }));
                setTableFilters(normalized);
            }
            : undefined,
        onRowSelectionChange: onRowSelectionChange ?
            (updatedSelection) => {
                const prev = rowSelection ?? {};
                const selectionObj =
                    typeof updatedSelection === 'function'
                        ? updatedSelection(prev)
                        : updatedSelection;

                onRowSelectionChange(selectionObj);
            }
            : undefined,

        renderRowActions,
        // Keep any external custom actions on the left; filter button will be on the right side toolbar actions
        renderTopToolbarCustomActions: renderTopToolbarCustomActions ?? (() => title? <span><Typography variant="subtitle1" sx={{ color: '#4F758B',fontWeight: 'bold',ml: 1 }}>{title}</Typography></span> : null),
        // Replace internal toolbar actions (right side) with our Filter button when using filter popover
        renderToolbarInternalActions: ({ table }) => (
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {enableGlobalSearch &&<MRT_ToggleGlobalFilterButton table={table} />}
                {enableFilterModal && enableFiltering && (
                    <Tooltip title="Open Filters">
                        <IconButton
                            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                            aria-label="open-filters"
                        >
                            <FilterListIcon />
                        </IconButton>
                    </Tooltip>
                )}
                {enableShowHideColumns && <MRT_ShowHideColumnsButton table={table} />}
                {enableDownloadCsv && (
                    <Tooltip title="Download csv">
                        <IconButton
                            aria-label="open-filters"
                            disabled = {table.getFilteredRowModel().rows.length === 0} // Disable if no data to download
                            onClick ={() => handleDownloadCsv()} 
                        >
                            <CloudDownload />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        ),
        // Add conditional row selection support
        muiSelectCheckboxProps: enableRowSelection ? (
            isRowSelectable ? ({ row }) => {
                const isSelectable = isRowSelectable(row.original);
                return {
                    disabled: !isSelectable,
                    style: {
                        opacity: isSelectable ? 1 : 0.5,
                    },
                    onClick: (event) => {
                        if (!isSelectable) {
                            event.preventDefault();
                            event.stopPropagation();
                            return false;
                        }
                    }
                };
            } : undefined
        ) : undefined,
        muiSelectAllCheckboxProps: enableRowSelection && 
            isRowSelectable && (({ table }) => {
                // Get all rows (not just page) to determine if all are selected
                const allLeafRows = table.getPrePaginationRowModel().flatRows;
                // Only rows that are selectable
                const selectableRows = allLeafRows.filter(
                    (r) => isRowSelectable?.(r.original) ?? true
                );
                const currentSelection = table.getState().rowSelection;

                // Only count selected rows that are selectable
                const selectedCount = selectableRows.filter((r) => currentSelection[r.id]).length;

                const allSelected = selectableRows.length > 0 && selectedCount === selectableRows.length;
                const someSelected = selectedCount > 0 && selectedCount < selectableRows.length;

                return {
                    disabled: selectableRows.length === 0,
                    checked: allSelected,
                    indeterminate: someSelected,
                    onChange: (e, checked) => {
                        e.stopPropagation();
                        let newSelection: Record<string, boolean> = { ...currentSelection };
                        if (checked) {
                            selectableRows.forEach((r) => {
                                newSelection[r.id] = true;
                            });
                        } else {
                            selectableRows.forEach((r) => {
                                delete newSelection[r.id];
                            });
                        }
                        table.setRowSelection(newSelection);
                        // Propagate to parent
                        if (typeof onRowSelectionChange === 'function') {
                            onRowSelectionChange(newSelection);
                        }
                    },
                    onClick: (e) => {
                        e.stopPropagation();
                    },
                };
            }
        ),
        muiTableProps: {
            ...muiTableProps,
            sx: {
                width: '100%',
                tableLayout: 'auto', // Force auto layout to allow header wrapping
                // Globally hide sort icons inside header sort labels while keeping sorting enabled
                '& .MuiTableSortLabel-root .MuiTableSortLabel-icon': {
                    display: 'none !important',
                },
                '& .MuiTableSortLabel-root .MuiTableSortLabel-iconDirectionAsc': {
                    display: 'none !important',
                },
                '& .MuiTableSortLabel-root .MuiTableSortLabel-iconDirectionDesc': {
                    display: 'none !important',
                },
                '& .MuiTableSortLabel-root svg': {
                    display: 'none !important',
                },
                ...muiTableProps?.sx,
            },
        },
        muiTableBodyRowProps: {
            ...muiTableBodyRowProps,
            // Only add onClick if onRowClick is provided and row selection is not enabled
            // to avoid conflicts between row selection and row click
            onClick: onRowClick && !enableRowSelection
                ? (event, { row }) => {
                    onRowClick(row);
                }
                : undefined,
            sx: {
                cursor: onRowClick && !enableRowSelection ? 'pointer' : 'default',
                '&:hover': {
                    backgroundColor: onRowClick && !enableRowSelection ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                },
                ...muiTableBodyRowProps?.sx,
            },
        },
        muiTableHeadCellProps: {
            ...muiTableHeadCellProps,
            sx: {
                fontWeight: 'bold',
                // Force header text to wrap at spaces
                whiteSpace: 'normal !important',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                wordBreak: 'normal',
                textOverflow: 'clip !important',
                overflow: 'visible !important',
                // Ensure headers can expand vertically
                height: 'auto !important',
                minHeight: 'auto !important',
                // Hide the up/down sort arrows but keep columns sortable
                '& .MuiTableSortLabel-root': {
                    whiteSpace: 'normal !important',
                    overflow: 'visible !important',
                    textOverflow: 'clip !important',
                    display: 'block',
                    // common icon container
                    '& .MuiTableSortLabel-icon': { display: 'none !important' },
                    '& .MuiTableSortLabel-iconDirectionAsc': { display: 'none !important' },
                    '& .MuiTableSortLabel-iconDirectionDesc': { display: 'none !important' },
                    // catch-all for any svg icons inside the sort label
                    '& svg': { display: 'none !important' },
                },
                ...muiTableHeadCellProps?.sx,
            },
        },
        muiTableBodyCellProps: {
            ...muiTableBodyCellProps,
            sx: {
                // Always apply text wrapping so content fits within max widths
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                ...muiTableBodyCellProps?.sx,
            },
        },
        muiPaginationProps: {
            rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100],
            ...muiPaginationProps,
        },
        state: {
            isLoading,
            rowSelection: rowSelection || {},
            // If parent is controlling filters, don't override internal unless undefined
            ...(initialState?.columnFilters ? { columnFilters: initialState.columnFilters } : {}),
        },
        localization: {
            noRecordsToDisplay: emptyMessage,
        },
    };

    const table = useMaterialReactTable(tableOptions);

    // Filter Popover (anchored by the button)
    const FilterPopover = () => {
        if (!enableFiltering) return null; // respect table-level filtering disabled
        // Include hidden columns by using all leaf columns, not just visible headers
        let filterableColumns = table
            .getAllLeafColumns()
            .filter((col) => {
                const hasAccessor = !!col.columnDef.accessorKey;
                const colEnabled = (col.columnDef.enableColumnFilter ?? true);
                // also consult MRT's canFilter which respects table-level state
                const canFilter = typeof col.getCanFilter === 'function' ? col.getCanFilter() : true;
                return hasAccessor && colEnabled && canFilter;
            });

        // No special carveouts or column-id constraints: show all columns with filtering enabled

        // Generate unique options for each filterable column
        const getColumnOptions = (columnId: string, accessorKey: string) => {
            const uniqueValues = new Set<string>();
            data.forEach(row => {
                // Try both the columnId and accessorKey to get the value
                const value = row[columnId] || row[accessorKey];
                if (value !== null && value !== undefined && value !== '') {
                    uniqueValues.add(String(value));
                }
            });
            const sortedValues = Array.from(uniqueValues).sort();
            return sortedValues;
        };

        return (
            <Popover
                open={isFilterPopoverOpen}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { p: 2, maxWidth: 720 } } }}
            >
                <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" component="div">{filterModalTitle}</Typography>
                    <Grid container spacing={2} sx={{ mt: 0.5, maxWidth: 720 }}>
                        {filterableColumns.length > 0 ? (
                            filterableColumns.map((col) => {
                                const columnId = col.id;
                                const accessorKey = col.columnDef.accessorKey as string;
                                const currentFilterValue = col.getFilterValue();
                                const options = getColumnOptions(columnId, accessorKey);

                                // Ensure filter value is always an array for multi-select
                                const selectedValues = Array.isArray(currentFilterValue)
                                    ? currentFilterValue
                                    : currentFilterValue
                                        ? [currentFilterValue]
                                        : [];

                                return (
                                    <Grid size={{xs: 12, sm: 6}} key={columnId}>
                                        <Autocomplete
                                            multiple
                                            fullWidth
                                            size="small"
                                            options={options}
                                            value={selectedValues}
                                            onChange={(event, newValue) => {
                                                // Set filter value as array for multi-select filtering
                                                col.setFilterValue(newValue.length > 0 ? newValue : undefined);
                                            }}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        key={option}
                                                        variant="outlined"
                                                        label={option}
                                                        size="small"
                                                        {...getTagProps({ index })}
                                                    />
                                                ))
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={`${col.columnDef.header}`}
                                                    placeholder={selectedValues.length === 0 ? "Select values..." : ""}
                                                    variant="outlined"
                                                />
                                            )}
                                            // Custom filter function to handle array includes
                                            filterOptions={(options, { inputValue }) => {
                                                return options.filter(option =>
                                                    option.toLowerCase().includes(inputValue.toLowerCase())
                                                );
                                            }}
                                        />
                                    </Grid>
                                );
                            })
                        ) : (
                            <Grid size={12}>
                                <Typography variant="body2" color="text.secondary">
                                    No filterable columns available.
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        <Button
                            onClick={() => {
                                // Clear all filters
                                table.resetColumnFilters();
                                if (setTableFilters) setTableFilters([]);
                            }}
                            color="secondary"
                        >
                            Clear All
                        </Button>
                        <Button
                            onClick={() => setFilterAnchorEl(null)}
                            color="primary"
                            variant="contained"
                        >
                            Close
                        </Button>
                    </Box>
                </MuiBox>
            </Popover>
        );
    };

    return (
        <Box>
            <MaterialReactTable table={table} />
            {enableFilterModal && <FilterPopover />}
        </Box>
    );
}

export default SharedMaterialReactTable;
